import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Feedback, useResource } from "@/components/workflow/shared";
import {
  equipmentE2Api,
  type EquipmentLookup,
  type EquipmentRequest,
  type E2Result,
} from "@/lib/api/oprema-e2";
import { siteEquipmentApi, type EquipmentResult } from "@/lib/api/oprema";
import { danas } from "@/lib/dan";
import { datum } from "@/lib/format";
import {
  E2DocumentActions,
  E2Errors,
  E2Notice,
  ProcessState,
  useE2Save,
} from "./E2Shared";
import {
  Field,
  Photos,
  Quantity,
  UndoNotice,
  useEquipmentAccess,
} from "./shared";
import {
  emptyProof,
  HandoverProofFields,
  proofInput,
  proofReady,
} from "./HandoverProof";
import { placeTargets, targetKey } from "./EquipmentForms";
import { EquipmentBack, useEquipmentNavigation } from "./EquipmentNavigation";
import { EquipmentDetail as EquipmentDetailSheet } from "./EquipmentList";
import {
  EquipmentDraft,
  useEquipmentDraftState,
  type EquipmentDraftCache,
} from "./EquipmentDraft";
import { Badge } from "@/components/ui/badge";

export function ClosureView({ placeId }: { placeId: string }) {
  const { user } = useAuth(),
    access = useEquipmentAccess(),
    save = useE2Save();
  const navigate = useNavigate();
  const navigation = useEquipmentNavigation();
  const exceptionOpen = navigation.params.get("radnja") === "iznimka";
  const resource = useResource(
    `closure:${placeId}`,
    useCallback((signal) => equipmentE2Api.closure(placeId, signal), [placeId]),
  );
  const [reason, setReason] = useState(""),
    [decision, setDecision] = useState("");
  const [operation, setOperation] = useState<E2Result | null>(null);
  const view = resource.data;
  const firstMissing = view?.koraci.find((step) => !step.gotovo);
  const actionRoute = (key: string) =>
    key.includes("inventura") || key === "razlike"
      ? `/oprema/mjesta/${placeId}/inventura`
      : key.includes("najam")
        ? `/oprema/najam?mjesto=${placeId}`
        : `/oprema/mjesta/${placeId}`;
  const closed = (row: { rezultat?: E2Result }) => {
    setOperation(row.rezultat ?? null);
    resource.reload();
  };
  return (
    <section className="site-equipment" aria-label="Zatvaranje gradilišta">
      <h2 className="text-xl font-bold">Zatvaranje gradilišta</h2>
      <Feedback error={resource.error} />
      {resource.loading && <p role="status">Provjera razduženja opreme…</p>}
      {view && <>
        <ProcessState state={!view.aktivno ? "zatvoreno" : view.zahtjev?.status === "ceka_upravu" ? "ceka_upravu" : "u_tijeku"} />
        <section className="equipment-step" aria-label="Sljedeći korak">
          <h3 className="font-bold">Sljedeći korak</h3>
          <p>{!view.aktivno ? "Gradilište je zatvoreno. Zapisnik je u dnevniku." : view.moze_zatvoriti ? "Sve je razduženo. Možeš zatvoriti gradilište." : "Razduži opremu i riješi otvorene stavke."}</p>
          {view.koraci.map((step, index) => (
              <div className={`equipment-row equipment-step ${step.gotovo ? "is-done" : firstMissing?.kljuc === step.kljuc ? "is-current" : "is-pending"}`} key={step.kljuc}>
              <span><span className="equipment-step-number">{index + 1}</span>{step.gotovo ? "✓" : "✗"} {step.naziv}</span>
              {!step.gotovo && <Button type="button" variant="outline" onClick={() => navigate(actionRoute(step.kljuc))}>{step.akcija || "Riješi"}</Button>}
            </div>
          ))}
          {view.aktivno && access.maintain && <Button disabled={save.busy || (!view.moze_zatvoriti && !firstMissing)} onClick={() => {
            if (!view.moze_zatvoriti && firstMissing) navigate(actionRoute(firstMissing.kljuc));
            else if (view.moze_zatvoriti) void save.run(() => equipmentE2Api.close(placeId), closed);
          }}>
            {save.busy ? "Spremam…" : view.moze_zatvoriti ? "Zatvori gradilište" : firstMissing?.akcija || "Razduži opremu"}
          </Button>}
        </section>
        {view.aktivno && access.maintain && !view.moze_zatvoriti && view.zahtjev?.status !== "ceka_upravu" && <>
          {!exceptionOpen ? <Button type="button" variant="outline" onClick={() => navigation.open({ radnja: "iznimka" })}>Zatvori bez razduženja</Button> : <form className="equipment-step" aria-label="Zatvori bez" onSubmit={(event) => {
            event.preventDefault();
            if (reason.trim()) void save.run(() => equipmentE2Api.close(placeId, reason.trim()), closed);
          }}>
            <EquipmentBack disabled={save.busy} onBack={() => navigation.close(["radnja"])} crumbs={[{ label: "Zatvaranje" }, { label: "Iznimka" }]} />
            <h3><span className="equipment-step-number">1</span> Razlog iznimke</h3>
            <p>Zatvaranje bez razduženja traži potvrdu druge osobe iz uprave.</p>
            <Field label="Razlog zatvaranja bez razduženja"><textarea required maxLength={2000} value={reason} onChange={(event) => setReason(event.target.value)} /></Field>
            <Button variant="outline" disabled={save.busy || !reason.trim()}>Zatraži potvrdu uprave</Button>
          </form>}
        </>}
        {view.zahtjev?.status === "ceka_upravu" && <section className="equipment-step">
          <h3><span className="equipment-step-number">2</span> Čeka potvrdu uprave</h3>
          <p>{view.zahtjev.razlog}</p>
          {access.approve && <>
            <Field label="Razlog odluke"><textarea required maxLength={2000} value={decision} onChange={(event) => setDecision(event.target.value)} /></Field>
            {view.zahtjev.zatrazio_id === user?.id && <p>Zahtjev mora odobriti druga osoba.</p>}
            <div className="equipment-place-actions">{[true, false].map((accept) => <Button variant="outline" key={String(accept)} disabled={save.busy || !decision.trim() || view.zahtjev?.zatrazio_id === user?.id} onClick={() => void save.run(() => equipmentE2Api.decideClosure(view.zahtjev!.id, accept, decision.trim()), closed)}>{accept ? "Potvrdi zatvaranje bez" : "Odbij iznimku"}</Button>)}</div>
          </>}
        </section>}
        <details><summary className="equipment-link">Dnevnik i zapisnik</summary>
          {view.zahtjev && <p>{view.zahtjev.razlog || "Redovno razduženje"}</p>}
          {view.dokument && <E2DocumentActions document={view.dokument} />}
        </details>
      </>}
      <E2Notice result={operation} message="Zapisnik o razduženju spremljen." onUndone={() => { setOperation(null); resource.reload(); }} />
      <E2Errors save={save} />
    </section>
  );
}

export function RequestViews({
  lookup,
  placeId,
}: {
  lookup: EquipmentLookup;
  placeId?: string;
}) {
  const access = useEquipmentAccess(),
    save = useE2Save();
  const resource = useResource(
    `requests:${placeId}`,
    useCallback(
      (signal) => equipmentE2Api.requests(placeId, signal),
      [placeId],
    ),
  );
  const navigation = useEquipmentNavigation();
  const navigate = useNavigate();
  const location = useLocation();
  const [drafts] = useState<EquipmentDraftCache>(() => new Map());
  const requestedForm = navigation.params.get("radnja") === "new";
  const requestId = navigation.params.get("zahtjev") ?? "";
  const dispatch = navigation.params.get("radnja") === "dispatch" ? requestId : "";
  const request = resource.data?.find((row) => row.id === requestId);
  const closeNew = () => navigation.close(["radnja"]);
  const openDispatch = (id: string) => {
    const params = new URLSearchParams(navigation.params);
    params.set("zahtjev", id);
    params.set("radnja", "dispatch");
    navigate(
      { pathname: location.pathname, search: `?${params}` },
      { state: { ...location.state, equipmentOpened: location.pathname } },
    );
  };
  const [place, setPlace] = useState(placeId ?? ""),
    [date, setDate] = useState(danas),
    [note, setNote] = useState("");
  const targetPlace = lookup.places.find(
    (row) => row.id === place && row.vrsta === "gradiliste" &&
      access.canMaintain(row) && (access.all || !!row.gradiliste_id),
  );
  const canRequest = access.maintain && !!targetPlace;
  const form = requestedForm && access.maintain && (!placeId || canRequest);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [operation, setOperation] = useState<{
    result: EquipmentResult;
    message: string;
  } | null>(null);
  const [at, setAt] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setAt(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const selected = lookup.articles.filter((a) => (quantities[a.id] ?? 0) > 0);
  return (
    <section className="site-equipment" aria-label="Zahtjevi iz Baze">
      <div className="equipment-row">
        <h2 className="text-xl font-bold">Zatraži iz Baze</h2>
        {access.maintain && (!placeId || canRequest) && !form && !request && (
          <Button onClick={() => navigation.open({ radnja: "new", zahtjev: null })}>Novi zahtjev</Button>
        )}
      </div>
      <Feedback error={resource.error} />
      <UndoNotice
        operation={operation}
        onUndone={() => {
          setOperation(null);
          resource.reload();
        }}
      />
      {form && (
        <form
          className="equipment-form"
          aria-label="Zatraži iz Baze"
          onSubmit={(e) => {
            e.preventDefault();
            if (!canRequest || !selected.length || date < danas() || selected.some((article) => !Number.isInteger(quantities[article.id]))) return;
            void save.run(
              () =>
                equipmentE2Api.request({
                  mjesto_id: place,
                  potrebno_do: date,
                  napomena: note || null,
                  stavke: selected.map((a) => ({
                    artikl_id: a.id,
                    kolicina: quantities[a.id],
                  })),
                }),
              () => {
                closeNew();
                setQuantities({});
                resource.reload();
              },
            );
          }}
        >
          <EquipmentBack disabled={save.busy} onBack={closeNew} crumbs={[{ label: "Zatraži iz Baze" }, { label: "Novi zahtjev" }]} />
          <fieldset className={`equipment-step ${canRequest && date >= danas() ? "is-done" : ""}`}>
            <legend><span className="equipment-step-number">1</span> Kamo i do kada</legend>
          <Field label="Gradilište zahtjeva">
            <select
              required
              disabled={!!placeId}
              value={place}
              onChange={(e) => setPlace(e.target.value)}
            >
              <option value="">Odaberi gradilište</option>
              {lookup.places
                .filter(
                  (p) =>
                    p.vrsta === "gradiliste" &&
                    access.canMaintain(p) &&
                    (access.all || !!p.gradiliste_id),
                )
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.naziv}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Potrebno do">
            <input
              required
              type="date"
              min={danas()}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          <Field label="Napomena (neobavezno)">
            <textarea
              maxLength={2000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Field>
          </fieldset>
          <fieldset className={`equipment-step ${selected.length > 0 ? "is-done" : ""}`}>
            <legend><span className="equipment-step-number">2</span> Što trebaš</legend>
          {lookup.articles
            .filter((a) => a.aktivna)
            .map((a) => (
              <article className="equipment-transfer-line" key={a.id}>
                <b>{a.naziv}</b>
                <Quantity
                  label={`Zatraži: ${a.naziv}`}
                  value={quantities[a.id] ?? 0}
                  onChange={(n) =>
                    setQuantities((old) => ({ ...old, [a.id]: n }))
                  }
                />
              </article>
            ))}
          </fieldset>
          <E2Errors save={save} />
          <Button
            disabled={
              save.busy ||
              !canRequest ||
              !selected.length ||
              date < danas() ||
              selected.some((a) => !Number.isInteger(quantities[a.id]))
            }
          >
            {save.busy ? "Šaljem…" : !canRequest ? "Odaberi gradilište" : !selected.length ? "Odaberi što trebaš" : date < danas() ? "Upiši datum" : selected.some((a) => !Number.isInteger(quantities[a.id])) ? "Upiši cijele količine" : "Pošalji zahtjev u Bazu"}
          </Button>
        </form>
      )}
      {resource.loading && <p role="status">Učitavanje zahtjeva…</p>}
      {!form && !request && <div className="equipment-place-list">
        {resource.data?.map((row) => <Button
          key={row.id} variant="outline" className="equipment-placecard"
          aria-label={`Otvori zahtjev: ${lookup.places.find((p) => p.id === row.mjesto_id)?.naziv ?? "Gradilište"} · ${datum(row.potrebno_do)}`}
          onClick={() => navigation.open({ zahtjev: row.id, radnja: null })}
        >
          <span className="equipment-list-name"><b>{lookup.places.find((p) => p.id === row.mjesto_id)?.naziv ?? "Gradilište"}</b><small>Potrebno {datum(row.potrebno_do)} · {row.stavke.length} vrsta</small></span>
          <Badge tone={row.status === "otvoren" ? "warn" : row.status === "poslano" ? "info" : "neutral"}>{row.status === "otvoren" ? "Čeka Bazu" : row.status === "poslano" ? "Poslano" : "Poništeno"}</Badge>
          <span aria-hidden="true">›</span>
        </Button>)}
      </div>}
      {!form && request && <EquipmentDetailSheet title="Zahtjev iz Baze" onClose={() => navigation.close(["zahtjev", "radnja"])} crumbs={[{ label: "Zatraži iz Baze", to: placeId ? `/oprema/mjesta/${placeId}/zahtjevi` : "/oprema/zahtjevi" }, { label: "Zahtjev" }]}>
        <div className="equipment-step">
          <h3 className="font-bold">{lookup.places.find((p) => p.id === request.mjesto_id)?.naziv ?? "Gradilište"}</h3>
          <Badge tone={request.status === "otvoren" ? "warn" : request.status === "poslano" ? "info" : "neutral"}>{request.status === "otvoren" ? "Čeka Bazu" : request.status === "poslano" ? "Poslano iz Baze" : "Poništeno"}</Badge>
          <p>Potrebno {datum(request.potrebno_do)}</p>
          {request.stavke.map((line) => <div className="equipment-row equipment-transfer-line" key={line.id}><b>{line.naziv}</b><strong>{line.kolicina}</strong></div>)}
          {request.napomena && <p>{request.napomena}</p>}
          {request.status === "otvoren" && !dispatch && <div className="equipment-place-actions">
            {access.all && <Button onClick={() => openDispatch(request.id)}>Zaduži iz Baze</Button>}
            {access.maintain && at - Date.parse(request.created_at) <= 120_000 && <Button variant="outline" disabled={save.busy} onClick={() => void save.run(() => equipmentE2Api.cancelRequest(request.id), resource.reload)}>Poništi zahtjev (120 s)</Button>}
          </div>}
        </div>
        {dispatch === request.id && access.all && access.maintain && <EquipmentDraft key={request.id} name={`request-dispatch:${request.id}`} cache={drafts}>
          <RequestDispatchForm request={request} lookup={lookup} onSaved={(result) => {
            navigation.close(["radnja"]);
            setOperation({ result, message: "Poslano iz Baze, čeka potvrdu primitka." });
            resource.reload();
          }} />
        </EquipmentDraft>}
      </EquipmentDetailSheet>}
      {resource.data?.length === 0 && <p>Nema zahtjeva iz Baze.</p>}
      <E2Errors save={save} />
    </section>
  );
}

function RequestDispatchForm({
  request,
  lookup,
  onSaved,
}: {
  request: EquipmentRequest;
  lookup: EquipmentLookup;
  onSaved: (result: EquipmentResult) => void;
}) {
  const access = useEquipmentAccess(),
    save = useE2Save();
  const [base, setBase] = useEquipmentDraftState("base", ""),
    [counts, setCounts] = useEquipmentDraftState<Record<string, number>>("counts", {}),
    [photos, setPhotos] = useEquipmentDraftState<File[]>("photos", []),
    [proof, setProof] = useEquipmentDraftState("proof", emptyProof);
  const resource = useResource(
    `base:${base}`,
    useCallback(
      (signal) =>
        base ? siteEquipmentApi.place(base, signal) : Promise.resolve(null),
      [base],
    ),
  );
  const targets = resource.data
    ? placeTargets(resource.data).filter(
        (t) =>
          request.stavke.some((s) => s.artikl_id === t.article.id) &&
          (t.piece
            ? t.piece.stanje === "ispravno"
            : (t.balance?.ispravno ?? 0) > 0),
      )
    : [];
  const defaults: Record<string, number> = {};
  const remaining = new Map(
    request.stavke.map((line) => [line.artikl_id, line.kolicina]),
  );
  for (const target of targets) {
    const n = Math.min(
      remaining.get(target.article.id) ?? 0,
      target.piece ? 1 : target.balance!.ispravno,
    );
    defaults[targetKey(target)] = n;
    remaining.set(
      target.article.id,
      (remaining.get(target.article.id) ?? 0) - n,
    );
  }
  const quantityFor = (key: string) => counts[key] ?? defaults[key] ?? 0;
  const selected = targets.filter((t) => quantityFor(targetKey(t)) > 0);
  const exact = request.stavke.every(
    (line) =>
      selected
        .filter((t) => t.article.id === line.artikl_id)
        .reduce((n, t) => n + quantityFor(targetKey(t)), 0) === line.kolicina,
  );
  const valid =
    access.all &&
    access.maintain &&
    exact &&
    selected.length > 0 &&
    targets.every(
      (t) =>
        Number.isInteger(quantityFor(targetKey(t))) &&
        quantityFor(targetKey(t)) >= 0 &&
        quantityFor(targetKey(t)) <= (t.piece ? 1 : t.balance!.ispravno),
    ) &&
    proofReady(photos, proof, access.evidenceException);
  return (
    <form
      className="equipment-form"
      aria-label="Zaduži zahtjev iz Baze"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        void save.run(async () => {
          const raw = await equipmentE2Api.dispatch(
            request.id,
            {
              iz_mjesta_id: base,
              u_mjesto_id: request.mjesto_id,
              datum: danas(),
              stavke: selected.map((t) => ({
                artikl_id: t.article.id,
                komad_id: t.piece?.id ?? null,
                najam_id: t.piece?.najam_id ?? t.balance?.najam_id ?? null,
                kolicina: quantityFor(targetKey(t)),
                stanje: "ispravno",
              })),
              ...proofInput(photos, proof, access.evidenceException),
            },
            { photos, signature: proof.potpis },
          );
          const entry = raw.kretanja.find((e) =>
            ["zaduzenje", "selidba_poslano"].includes(e.vrsta),
          );
          if (!entry) throw new Error("Nedostaje zapis zaduženja.");
          return {
            id: entry.id,
            vrijeme: entry.vrijeme,
            status: entry.status,
            ponistivo_do: entry.ponisti_do,
            dokument: entry.dokument
              ? {
                  id: entry.dokument.id,
                  broj: entry.dokument.broj,
                  kretanje_id: entry.id,
                }
              : undefined,
          };
        }, onSaved);
      }}
    >
      <fieldset className={`equipment-step ${base ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">1</span> Iz koje Baze</legend>
      <Field label="Baza za zaduženje">
        <select
          required
          value={base}
          onChange={(e) => {
            setBase(e.target.value);
            setCounts({});
          }}
        >
          <option value="">Odaberi Bazu</option>
          {lookup.places
            .filter((p) => p.vrsta === "baza")
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.naziv}
              </option>
            ))}
        </select>
      </Field>
      </fieldset>
      <fieldset className={`equipment-step ${exact ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">2</span> Što šalješ</legend>
      <Feedback error={resource.error} />
      {targets.map((t) => (
        <article className="equipment-transfer-line" key={targetKey(t)}>
          <b>
            {t.article.naziv}
            {t.piece ? ` · ${t.piece.inventarni_broj}` : ""} ·{" "}
            {t.balance?.najam_id || t.piece?.najam_id ? "Najam" : "Vlastito"}
          </b>
          <Quantity
            label={`Pošalji: ${t.article.naziv}${t.piece ? ` ${t.piece.inventarni_broj}` : t.balance?.najam_id ? " najam" : " vlastito"}`}
            value={quantityFor(targetKey(t))}
            max={t.piece ? 1 : t.balance!.ispravno}
            onChange={(n) =>
              setCounts((old) => ({ ...old, [targetKey(t)]: n }))
            }
          />
        </article>
      ))}
      {base && !exact && (
        <p className="equipment-warning">
          Odaberi točno zatražene količine i komade.
        </p>
      )}
      </fieldset>
      <fieldset className={`equipment-step ${proofReady(photos, proof, access.evidenceException) ? "is-done" : ""}`}>
        <legend><span className="equipment-step-number">3</span> Fotka i potpis</legend>
      <Photos files={photos} onChange={setPhotos} required />
      <HandoverProofFields
        value={proof}
        onChange={setProof}
        label="Predao"
        photos={photos}
        allowException={access.evidenceException}
      />
      </fieldset>
      <E2Errors save={save} />
      <Button disabled={save.busy || !valid}>{save.busy ? "Šaljem…" : !base ? "Odaberi Bazu" : !exact ? "Odaberi zatraženu opremu" : !proofReady(photos, proof, access.evidenceException) ? "Dodaj fotku i potpis" : "Pošalji na gradilište"}</Button>
    </form>
  );
}
